<template>
  <div class="flex items-center gap-2 flex-wrap">
    <UInput
      v-model="title"
      icon="i-lucide-search"
      placeholder="Search by title"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-56 min-w-0"
      aria-label="Search by title"
    />
    <USelect
      v-if="!hideCategory"
      v-model="category"
      :items="categoryItems"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-31 min-w-0"
      aria-label="Filter by category"
    />
    <USelect
      v-model="language"
      :items="languageItems"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-31 min-w-0"
      aria-label="Filter by language"
    />
    <USelect
      v-if="!hidePlacement"
      v-model="placement"
      :items="placementItems"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-46 min-w-0"
      aria-label="Filter by difficulty or lesson"
    />
    <USelect
      v-model="genre"
      :items="genreItems"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-36 min-w-0"
      aria-label="Filter by genre"
    />
    <UInputMenu
      v-if="!hideStars"
      v-model="stars"
      value-key="value"
      create-item
      :items="starsItems"
      placeholder="All Stars"
      color="neutral"
      variant="outline"
      size="sm"
      class="max-w-36 min-w-0"
      aria-label="Filter by star count"
      @create="handleCreateStars"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue';
import type { SelectItem } from '@nuxt/ui';
import { useConvexQuery } from 'convex-vue';
import { api } from '../../convex/_generated/api';
import type { PieceCategory } from '../../convex/types';
import { PIECE_CATEGORIES, PIECE_CATEGORY_LABEL_PLURAL } from '@/utils/pieceCategory';
import { PIECE_LANGUAGE_ITEMS, type PieceLanguage } from '@/utils/pieceLanguage';
import { placementScaleFor, type PiecePlacementFilter, type PlacementScale } from '@/composables/usePieceFilters';
import { usePieceFilterItems, onCreateStarsFilter } from '@/composables/usePieceFilterItems';

// Genre/stars options come from whatever list the caller is filtering
// (client-side in ClassroomLobbyView/SongsPieceList, server-side facets in
// AdminPiecesView), so they're props rather than fetched in here.
const props = withDefaults(defineProps<{
  genreOptions: string[]
  starsOptions: number[]
  /** SongsPieceList is already scoped to one category/placement group. */
  hideCategory?: boolean
  hidePlacement?: boolean
  hideStars?: boolean
}>(), {
  hideCategory: false,
  hidePlacement: false,
  hideStars: false,
});

const title = defineModel<string>('title', { required: true });
const category = defineModel<PieceCategory | 'all'>('category', { default: 'all' });
const language = defineModel<PieceLanguage | 'all'>('language', { required: true });
const placement = defineModel<PiecePlacementFilter>('placement', { default: 'all' });
const genre = defineModel<string>('genre', { required: true });
const stars = defineModel<number | 'all'>('stars', { required: true });

const { data: difficulties } = useConvexQuery(api.difficulties.list, {});
const { data: lessons } = useConvexQuery(api.lessons.list, {});

const categoryItems: SelectItem[] = [
  { label: 'All Categories', value: 'all' },
  ...PIECE_CATEGORIES.map((value) => ({ label: PIECE_CATEGORY_LABEL_PLURAL[value], value })),
];

const languageItems: SelectItem[] = [
  { label: 'All Languages', value: 'all' },
  ...PIECE_LANGUAGE_ITEMS,
];

const ALL_PLACEMENTS_LABEL = {
  both: 'All Difficulties / Lessons',
  difficulty: 'All Difficulties',
  lesson: 'All Lessons',
} as const;

const activeScale = computed(() => placementScaleFor(category.value));

// Difficulties and lessons answer the same "where does this piece sit" question
// on two parallel scales, so they share one select — grouped, each in the
// admin-managed order the placement column sorts by, and narrowed to the scale
// the chosen category actually places its pieces on.
const placementItems = computed<SelectItem[][]>(() => [
  [{ label: ALL_PLACEMENTS_LABEL[activeScale.value ?? 'both'], value: 'all' }],
  ...placementGroup('Difficulties', 'difficulty', difficulties.value),
  ...placementGroup('Lessons', 'lesson', lessons.value),
]);

// A scale contributes no group when the category rules it out, or when it has
// no rows yet — a heading over nothing. Once narrowed to a single scale the
// heading goes too: there is no second group left to tell it apart from.
// Returns a list so the caller can spread the empty case away.
function placementGroup(
  label: string,
  scale: PlacementScale,
  rows?: { _id: string; name: string }[]
): SelectItem[][] {
  if (!rows?.length || (activeScale.value && activeScale.value !== scale)) return [];
  const items = rows.map((row) => ({ label: row.name, value: `${scale}:${row._id}` }));
  return [activeScale.value ? items : [{ type: 'label', label }, ...items]];
}

const { genreItems, starsItems } = usePieceFilterItems(toRef(props, 'genreOptions'), toRef(props, 'starsOptions'));
const handleCreateStars = (item: string) => onCreateStarsFilter(stars, item);
</script>
